import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Building2, ArrowLeft, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function RegisterEmployee({ onBackToHome, onNavigateToLogin }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    pharmacy_id: '',
    username: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'caissier'  // Par défaut
  });

  useEffect(() => {
    fetchPharmacies();
  }, []);

  const fetchPharmacies = async () => {
    try {
      const response = await axios.get(`${API}/pharmacies`);
      // Only show active pharmacies
      setPharmacies(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      toast.error('Erreur lors du chargement des pharmacies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register-employee`, formData);
      toast.success('Demande envoyée avec succès !');
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
            <CardTitle className="text-2xl text-blue-600">Demande Envoyée !</CardTitle>
            <CardDescription>
              Votre demande d'inscription a été envoyée à l'administrateur de la pharmacie.<br />
              Vous pourrez vous connecter une fois votre compte validé.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={onNavigateToLogin}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600"
            >
              Aller à la Connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={onBackToHome}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Inscription Employé</CardTitle>
              <CardDescription>Rejoignez votre pharmacie en créant un compte</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Pharmacy Selection */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-blue-800 font-semibold">
                <Building2 className="w-5 h-5" />
                <span>Sélection de la Pharmacie</span>
              </div>
              
              <div>
                <Label>Pharmacie *</Label>
                <select
                  className="w-full border rounded-md p-2 mt-1"
                  value={formData.pharmacy_id}
                  onChange={(e) => setFormData({...formData, pharmacy_id: e.target.value})}
                  required
                >
                  <option value="">Sélectionnez votre pharmacie</option>
                  {pharmacies.map(pharmacy => (
                    <option key={pharmacy.id} value={pharmacy.id}>
                      {pharmacy.name} - {pharmacy.address}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  Sélectionnez la pharmacie où vous travaillez
                </p>
              </div>
            </div>

            {/* User Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-800 font-semibold">
                <User className="w-5 h-5" />
                <span>Vos Informations</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom Complet *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder="Ex: Ahmed Benali"
                    required
                  />
                </div>

                <div>
                  <Label>Nom d'Utilisateur *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    placeholder="Ex: ahmed.benali"
                    required
                  />
                </div>

                <div>
                  <Label>Mot de Passe *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Minimum 6 caractères"
                    required
                    minLength={6}
                  />
                </div>

                <div className="col-span-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+213 XXX XXX XXX"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Rôle / Fonction *</Label>
                  <select
                    className="w-full border rounded-md p-2 mt-1"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                  >
                    <option value="caissier">Caissier</option>
                    <option value="pharmacien">Pharmacien</option>
                    <option value="comptable">Comptable</option>
                    <option value="manager">Manager</option>
                    <option value="assistant">Assistant</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Important :</strong> Votre compte sera en attente de validation manuelle par l'administrateur de la pharmacie. Connectez-vous régulièrement pour vérifier si votre compte a été activé.
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-lg py-6"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer la Demande'}
            </Button>

            <p className="text-center text-sm text-gray-600">
              Vous avez déjà un compte ?{' '}
              <button 
                type="button"
                onClick={onNavigateToLogin} 
                className="text-blue-600 hover:underline font-semibold"
              >
                Se connecter
              </button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default RegisterEmployee;
